import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Circle, ArrowLeft, Trophy, Star, Volume2,
  RotateCcw, Heart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const PhonicsPopGame = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [gameState, setGameState] = useState('ready');
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [lives, setLives] = useState(3);
  const [targetSound, setTargetSound] = useState(null);
  const [balloons, setBalloons] = useState([]);
  const [poppedCount, setPoppedCount] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [isPlaying, setIsPlaying] = useState(false);

  // Letter sounds by level
  const soundGroups = {
    1: [
      { letter: 'S', sound: 'sss', words: ['sun', 'sit', 'sad'], color: 'bg-red-400' },
      { letter: 'A', sound: 'aaa', words: ['ant', 'apple', 'at'], color: 'bg-blue-400' },
      { letter: 'T', sound: 'tuh', words: ['tap', 'ten', 'top'], color: 'bg-green-400' },
      { letter: 'P', sound: 'puh', words: ['pin', 'pat', 'pop'], color: 'bg-yellow-400' },
      { letter: 'I', sound: 'ih', words: ['in', 'it', 'is'], color: 'bg-purple-400' },
      { letter: 'N', sound: 'nnn', words: ['nap', 'nut', 'net'], color: 'bg-pink-400' },
    ],
    2: [
      { letter: 'M', sound: 'mmm', words: ['map', 'mat', 'mum'], color: 'bg-orange-400' },
      { letter: 'D', sound: 'duh', words: ['dog', 'dad', 'dig'], color: 'bg-teal-400' },
      { letter: 'G', sound: 'guh', words: ['get', 'got', 'gap'], color: 'bg-indigo-400' },
      { letter: 'O', sound: 'oh', words: ['on', 'off', 'odd'], color: 'bg-rose-400' },
      { letter: 'C', sound: 'kuh', words: ['cat', 'cup', 'can'], color: 'bg-cyan-400' },
      { letter: 'K', sound: 'kuh', words: ['kit', 'kid', 'keg'], color: 'bg-lime-400' },
    ],
    3: [
      { letter: 'E', sound: 'eh', words: ['egg', 'end', 'elf'], color: 'bg-amber-400' },
      { letter: 'U', sound: 'uh', words: ['up', 'us', 'under'], color: 'bg-violet-400' },
      { letter: 'R', sound: 'rrr', words: ['run', 'red', 'rat'], color: 'bg-fuchsia-400' },
      { letter: 'H', sound: 'huh', words: ['hat', 'hit', 'hop'], color: 'bg-emerald-400' },
      { letter: 'B', sound: 'buh', words: ['bat', 'big', 'bus'], color: 'bg-sky-400' },
      { letter: 'F', sound: 'fff', words: ['fan', 'fun', 'fit'], color: 'bg-red-300' },
    ]
  };

  const generateRound = useCallback(() => {
    const sounds = soundGroups[Math.min(level, 3)];
    const target = sounds[Math.floor(Math.random() * sounds.length)];
    
    // Create balloons - some with target letter, some with others
    const newBalloons = [];
    const targetCount = 3 + level; // More targets at higher levels
    const wrongCount = 6 - level; // Fewer wrong at higher levels
    
    // Add target balloons
    for (let i = 0; i < targetCount; i++) {
      newBalloons.push({
        id: `target-${i}`,
        letter: target.letter,
        sound: target.sound,
        isTarget: true,
        x: Math.random() * 80 + 10,
        y: Math.random() * 60 + 20,
        color: target.color,
        popped: false,
        floatDelay: Math.random() * 2
      });
    }
    
    // Add wrong balloons
    const wrongSounds = sounds.filter(s => s.letter !== target.letter);
    for (let i = 0; i < wrongCount; i++) {
      const wrong = wrongSounds[Math.floor(Math.random() * wrongSounds.length)];
      newBalloons.push({
        id: `wrong-${i}`,
        letter: wrong.letter,
        sound: wrong.sound,
        isTarget: false,
        x: Math.random() * 80 + 10,
        y: Math.random() * 60 + 20,
        color: wrong.color,
        popped: false,
        floatDelay: Math.random() * 2
      });
    }
    
    setTargetSound(target);
    setBalloons(newBalloons.sort(() => Math.random() - 0.5));
    setTimeLeft(30);
  }, [level]);

  const playSound = async (text) => {
    if (isPlaying) return;
    setIsPlaying(true);
    
    try {
      const response = await api.post('/v2/media/tts/generate', {
        text: text,
        voice: 'fable'  // British accent
      });
      
      if (response.data?.audio) {
        const audio = new Audio(`data:audio/mp3;base64,${response.data.audio}`);
        audio.onended = () => setIsPlaying(false);
        audio.play();
      } else {
        setIsPlaying(false);
      }
    } catch (error) {
      console.error('TTS error:', error);
      setIsPlaying(false);
      // Fallback to browser speech synthesis
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.8;
      utterance.lang = 'en-GB';  // British accent
      utterance.onend = () => setIsPlaying(false);
      speechSynthesis.speak(utterance);
    }
  };

  const startGame = useCallback(() => {
    setGameState('playing');
    setScore(0);
    setLives(3);
    setLevel(1);
    setPoppedCount(0);
    generateRound();
  }, [generateRound]);

  // Timer
  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(t => t - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && gameState === 'playing') {
      // Time's up for this round - move to next
      const remainingTargets = balloons.filter(b => b.isTarget && !b.popped).length;
      if (remainingTargets > 0) {
        setLives(l => {
          const newLives = l - 1;
          if (newLives <= 0) {
            setGameState('finished');
          } else {
            generateRound();
          }
          return newLives;
        });
        toast.error(`Time's up! ${remainingTargets} balloons left!`);
      }
    }
  }, [gameState, timeLeft, balloons, generateRound]);

  // Check if all target balloons are popped
  useEffect(() => {
    if (gameState !== 'playing' || !balloons.length) return;
    
    const remainingTargets = balloons.filter(b => b.isTarget && !b.popped).length;
    if (remainingTargets === 0) {
      // Round complete!
      const bonus = timeLeft * 2;
      setScore(s => s + bonus);
      toast.success(`Round complete! +${bonus} time bonus!`);
      
      if ((poppedCount + 1) % 10 === 0 && level < 3) {
        setLevel(l => l + 1);
        toast.success(`Level ${level + 1}! New sounds!`);
      }
      
      setTimeout(() => generateRound(), 1000);
    }
  }, [balloons, gameState, timeLeft, poppedCount, level, generateRound]);

  const popBalloon = (balloon) => {
    if (balloon.popped) return;
    
    if (balloon.isTarget) {
      // Correct!
      setBalloons(prev => prev.map(b => 
        b.id === balloon.id ? { ...b, popped: true } : b
      ));
      setScore(s => s + 15);
      setPoppedCount(c => c + 1);
      toast.success(`+15! ${balloon.letter} says "${balloon.sound}"!`);
    } else {
      // Wrong balloon
      setLives(l => {
        const newLives = l - 1;
        if (newLives <= 0) {
          setGameState('finished');
        }
        return newLives;
      });
      toast.error(`Oops! That's the "${balloon.sound}" sound, not "${targetSound.sound}"!`);
    }
  };

  // Save game on finish
  useEffect(() => {
    if (gameState === 'finished') {
      const saveGame = async () => {
        try {
          await api.post('/games/complete', {
            game_id: 'phonics-pop',
            score: score,
            time_seconds: 0,
            metadata: { level, balloons_popped: poppedCount }
          });
        } catch (error) {
          console.error('Error saving game:', error);
        }
      };
      saveGame();
    }
  }, [gameState, score, level, poppedCount, api]);

  return (
    <StudentLayout>
      <div data-testid="phonics-pop-game" className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={() => navigate('/student/games')}
            className="flex items-center gap-2 text-slate-600 hover:text-pink-600 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Games
          </button>
          
          {gameState === 'playing' && (
            <div className="flex items-center gap-4">
              <div className={`px-3 py-1 rounded-full ${timeLeft <= 10 ? 'bg-red-100' : 'bg-sky-100'}`}>
                <span className={`font-bold ${timeLeft <= 10 ? 'text-red-600' : 'text-sky-600'}`}>
                  {timeLeft}s
                </span>
              </div>
              <div className="flex items-center gap-1">
                {[...Array(3)].map((_, i) => (
                  <Heart key={i} className={`w-6 h-6 ${i < lives ? 'text-red-500 fill-red-500' : 'text-slate-300'}`} />
                ))}
              </div>
              <div className="flex items-center gap-2 bg-amber-100 px-4 py-2 rounded-full">
                <Star className="w-5 h-5 text-amber-600" />
                <span className="font-heading text-xl font-bold text-amber-700">{score}</span>
              </div>
            </div>
          )}
        </div>

        {/* Game Title */}
        <div className="student-card bg-gradient-to-r from-pink-100 to-rose-100 border-pink-300 mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-pink-500 rounded-2xl border-2 border-gray-800 flex items-center justify-center text-3xl">
              🎈
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-800">Phonics Pop</h1>
              <p className="text-slate-600">
                {gameState === 'playing' && targetSound ? `Pop the "${targetSound.sound}" balloons!` : 'Pop balloons with letter sounds!'}
              </p>
            </div>
          </div>
        </div>

        {/* Ready Screen */}
        {gameState === 'ready' && (
          <div className="student-card text-center py-12 bg-gradient-to-b from-pink-400 to-rose-500">
            <div className="flex justify-center gap-2 mb-6 text-6xl">
              <span className="animate-bounce" style={{ animationDelay: '0s' }}>🎈</span>
              <span className="animate-bounce" style={{ animationDelay: '0.2s' }}>🎈</span>
              <span className="animate-bounce" style={{ animationDelay: '0.4s' }}>🎈</span>
            </div>
            <h2 className="font-heading text-3xl font-bold text-white mb-4">Phonics Pop</h2>
            <p className="text-lg text-pink-100 mb-6">
              Listen to the sound and pop only the matching balloons!
            </p>
            <div className="text-pink-100 mb-8 space-y-2">
              <p>🔊 Click the speaker to hear the sound</p>
              <p>🎈 Pop balloons with that letter</p>
              <p>❌ Don't pop the wrong ones!</p>
            </div>
            <Button onClick={startGame} className="bg-white text-pink-600 hover:bg-pink-50 text-lg px-10 py-6">
              Start Popping!
              <Circle className="w-6 h-6 ml-2" />
            </Button>
          </div>
        )}

        {/* Playing Screen */}
        {gameState === 'playing' && targetSound && (
          <div className="student-card bg-gradient-to-b from-sky-300 to-sky-400 min-h-[450px] relative overflow-hidden">
            {/* Clouds */}
            <div className="absolute top-4 left-4 text-6xl opacity-50">☁️</div>
            <div className="absolute top-8 right-8 text-4xl opacity-50">☁️</div>
            
            {/* Target Sound Display */}
            <div className="text-center mb-4 relative z-10">
              <p className="text-sky-800 text-sm mb-2">Pop all the balloons with:</p>
              <button
                onClick={() => playSound(`${targetSound.sound}. Find all the ${targetSound.letter} balloons.`)}
                className={`bg-white rounded-xl px-6 py-3 shadow-lg inline-flex items-center gap-3 hover:scale-105 transition-all ${isPlaying ? 'animate-pulse' : ''}`}
              >
                <Volume2 className="w-6 h-6 text-pink-500" />
                <span className="text-4xl font-heading font-bold text-pink-600">{targetSound.letter}</span>
                <span className="text-pink-400">"{targetSound.sound}"</span>
              </button>
            </div>

            {/* Balloons Area */}
            <div className="relative h-72">
              {balloons.map((balloon) => (
                <button
                  key={balloon.id}
                  onClick={() => popBalloon(balloon)}
                  disabled={balloon.popped}
                  className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all ${
                    balloon.popped 
                      ? 'scale-0 opacity-0' 
                      : 'hover:scale-110 animate-float'
                  }`}
                  style={{
                    left: `${balloon.x}%`,
                    top: `${balloon.y}%`,
                    animationDelay: `${balloon.floatDelay}s`
                  }}
                >
                  <div className={`relative w-16 h-20 ${balloon.color} rounded-full shadow-lg flex items-center justify-center`}>
                    <span className="text-2xl font-bold text-white">{balloon.letter}</span>
                    {/* Balloon string */}
                    <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-0.5 h-4 bg-gray-400"></div>
                  </div>
                </button>
              ))}
            </div>

            {/* Progress */}
            <div className="text-center text-sky-800 text-sm">
              {balloons.filter(b => b.isTarget && !b.popped).length} balloons left to pop!
            </div>
          </div>
        )}

        {/* Finished Screen */}
        {gameState === 'finished' && (
          <div className="student-card text-center py-12 bg-gradient-to-b from-pink-400 to-rose-500">
            <div className="w-24 h-24 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <Trophy className="w-14 h-14 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-white mb-2">Great Popping!</h2>
            <p className="text-xl text-pink-100 mb-4">
              You popped {poppedCount} balloons!
            </p>
            
            <div className="flex justify-center gap-2 my-6 text-4xl">
              {[...Array(Math.min(Math.floor(poppedCount / 3), 5))].map((_, i) => (
                <span key={i} className="animate-bounce" style={{ animationDelay: `${i * 0.1}s` }}>🎈</span>
              ))}
            </div>
            
            <div className="flex items-center justify-center gap-2 mb-8">
              <Star className="w-8 h-8 text-amber-300 fill-amber-300" />
              <span className="font-heading text-4xl font-bold text-white">+{score}</span>
              <span className="text-pink-100 text-xl">points</span>
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={startGame} className="border-white text-white hover:bg-white/20">
                <RotateCcw className="w-4 h-4 mr-2" />
                Play Again
              </Button>
              <Button onClick={() => navigate('/student/games')} className="bg-white text-pink-600 hover:bg-pink-50">
                More Games
              </Button>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default PhonicsPopGame;
